import type { DatabaseReference, Unsubscribe } from "firebase/database";
import { get, onValue, runTransaction, set, update } from "firebase/database";
import type { PartialDeep } from "type-fest";
import type { Store } from "./Store";

export class FirebaseStore<T extends Record<string, any>> implements Store<T> {
  protected _ref: DatabaseReference;
  private _data: T | null = null;
  private _state: "idle" | "subscribed" = "idle";
  private _subscribers = new Set<(data: T | null) => void>();
  private _unsubscribeFromFirebase: Unsubscribe | null = null;

  constructor(ref: DatabaseReference) {
    this._ref = ref;
  }

  async get() {
    if (this._state === "subscribed") {
      return this._data;
    }

    const snapshot = await get(this._ref);
    return snapshot.exists() ? (snapshot.val() as T) : null;
  }

  async update(partialData: PartialDeep<T>) {
    return await update(this._ref, partialData);
  }

  async set(data: T | null) {
    return await set(this._ref, data);
  }

  /**
   * Runs a transaction against this reference.
   *
   * @note The callback runs against the *locally cached* value first and re-runs with server
   * data when that view turns out to be stale, so it must tolerate a `null` it did not expect.
   * Always return a value: aborting (returning `undefined`) short-circuits before the server is
   * ever consulted, so an unsynced path would act on a `null` that isn't real.
   */
  async transaction(callback: (data: T | null) => T | null) {
    await runTransaction(this._ref, callback);
  }

  subscribe(callback: (data: T | null) => void) {
    this._subscribers.add(callback);

    // A subscriber joining a store that is already listening gets the current value straight
    // away, rather than waiting for the next change at this path. Only the new callback is
    // called: the existing subscribers were handed this value when it arrived.
    if (this._state === "subscribed") {
      callback(this._data);
    }

    // `onValue` delivers the value at this path as its first event, so the listener alone is
    // enough to populate `_data`. Priming it with a one-shot `get()` used to race the listener:
    // `get()` forces a server round trip, while a write made from this client is applied locally
    // and reported to the listener straight away — so the read routinely resolved *after* the
    // listener had delivered a newer value, and overwrote `_data` with its older snapshot.
    // `_state` was "subscribed" by then, so every later `get()` returned that stale value until
    // the next change at this path happened to fire — indefinitely, if none ever did.
    this._unsubscribeFromFirebase ??= onValue(this._ref, (snapshot) => {
      this._data = snapshot.val() ?? null;
      this._state = "subscribed";
      this._subscribers.forEach((subscriber) => subscriber(this._data));
    });

    return () => {
      this._subscribers.delete(callback);

      if (this._subscribers.size === 0) {
        this.disconnect();
      }
    };
  }

  disconnect() {
    this._subscribers.clear();
    this._unsubscribeFromFirebase?.();
    this._unsubscribeFromFirebase = null;
    this._state = "idle";
    this._data = null;
  }
}
